import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Spinner,
  Text,
  ModalCloseButton,
  ModalFooter,
  Image,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faCapsules,
  faMoneyBillWave,
  faDollarSign,
  faPrint,
  faVial,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker, { convertToThDate } from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import Dropzone from "react-dropzone";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import { isValid } from "date-fns";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// Utility function to convert number to Thai text
const numberToThaiText = (number) => {
  const thaiDigits = [
    "ศูนย์",
    "หนึ่ง",
    "สอง",
    "สาม",
    "สี่",
    "ห้า",
    "หก",
    "เจ็ด",
    "แปด",
    "เก้า",
  ];
  const thaiUnits = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];
  const thaiBahtUnits = ["บาท", "สตางค์"];

  if (number === 0) return "ศูนย์บาทถ้วน";

  const formatNumber = (num, unitIndex) => {
    let result = "";
    const digits = num.toString().split("").reverse();
    for (let i = 0; i < digits.length; i++) {
      const digit = parseInt(digits[i]);
      if (digit !== 0) {
        if (i === 1 && digit === 1) {
          result = "สิบ" + result;
        } else if (i === 1 && digit === 2) {
          result = "ยี่สิบ" + result;
        } else {
          result = thaiDigits[digit] + thaiUnits[i] + result;
        }
      }
    }
    return result;
  };

  const [baht, satang] = number.toFixed(2).split(".");
  let bahtText = "";
  let satangText = "";

  if (parseInt(baht) > 0) {
    bahtText = formatNumber(parseInt(baht), 0) + thaiBahtUnits[0];
  }

  if (parseInt(satang) > 0) {
    satangText = formatNumber(parseInt(satang), 0) + thaiBahtUnits[1];
  }

  let result = bahtText;
  if (satangText) {
    result += satangText;
  } else {
    result += "ถ้วน";
  }

  return result || "ศูนย์บาทถ้วน";
};

const VaccineSaleForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "SALE-XXXX-XX-XX",
    FarmerID: "",
    FarmerName: "",
    FarmerTel: "",
    FarmerEmail: "",
    UsageObjective: "",
    PickupPoint: "",
    ReservedBy: profile?.Email || "",
    RequestDate: new Date(),
    VaccineDetails: [],
    paymentEvidence: [], // Array to store uploaded files
  });

  const [pickupPoints, setPickupPoints] = useState([]);
  const [newVaccine, setNewVaccine] = useState({
    id: "",
    lotId: "",
    unitreq: "",
  });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false); // New loading state for Lot inventory
  const [isExisting, setIsExisting] = useState(!!transactionId);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // State ใหม่สำหรับ Modal ยืนยัน

  // Fetch Lot inventory for a specific vaccine and warehouse
  const fetchLotInventory = async (vaccineId, warehouseId) => {
    console.log("Fetching Lot inventory for:", {
      vaccineId,
      warehouseId,
      sessionId,
    });
    if (!vaccineId || !warehouseId || !sessionId) {
      console.warn("Missing required parameters for fetchLotInventory:", {
        vaccineId,
        warehouseId,
        sessionId,
      });
      return;
    }

    setIsInventoryLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByLot",
        data: { VaccineID: vaccineId, WarehouseID: warehouseId },
        sessionId: sessionId || "",
      });
      console.log("getVaccineInventoryByLot response:", response);
      if (response.result === "success" && response.InventoryReport) {
        // Append new Lot data, avoiding duplicates
        setInventory((prev) => {
          const existingLots = new Set(
            prev.map((item) => `${item.VaccineID}-${item.LotID}`)
          );
          const newLots = response.InventoryReport.filter(
            (item) => !existingLots.has(`${item.VaccineID}-${item.LotID}`)
          );
          return [...prev, ...newLots];
        });
      } else {
        console.warn("API returned non-success result:", response);
      }
    } catch (error) {
      console.error("Error fetching lot inventory:", error.message);
    } finally {
      setIsInventoryLoading(false);
    }
  };

  // Update useEffect to fetch initial data
  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        console.log("Loading initial data with sessionId:", sessionId);
        await fetchPickupPoints();
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchSale(transactionId, vaccines);
        }
      } else {
        console.warn("No sessionId, redirecting to login");
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  // useEffect for fetching Lot inventory
  useEffect(() => {
    console.log("useEffect triggered for inventory fetch:", {
      vaccineId: newVaccine.id,
      pickupPoint: formData.PickupPoint,
    });
    if (newVaccine.id && formData.PickupPoint && sessionId) {
      fetchLotInventory(newVaccine.id, formData.PickupPoint);
    }
  }, [newVaccine.id, formData.PickupPoint, sessionId]);

  // Handle file drop for payment evidence
  const handleFileDrop = (acceptedFiles) => {
    const maxFileSize = 2 * 1024 * 1024; // 2MB in bytes (เพิ่มขนาดขึ้น)
    
    acceptedFiles.forEach((file) => {
      // ตรวจสอบขนาดไฟล์
      if (file.size > maxFileSize) {
        alert(`ไฟล์ "${file.name}" มีขนาดใหญ่เกินไป (${(file.size / 1024 / 1024).toFixed(2)}MB) กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 2MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        
        // ตรวจสอบขนาดของ base64 data
        const base64Size = base64Data.length;
        console.log("Base64 data size:", base64Size, "characters");
        
        if (base64Size > 1000000) { // 1MB limit for base64 (เพิ่มขนาดขึ้น)
          alert(`ไฟล์ "${file.name}" หลังจากแปลงเป็น base64 มีขนาดใหญ่เกินไป กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 2MB`);
          return;
        }
        
        const fileData = {
          name: file.name,
          type: file.type,
          data: base64Data,
          size: file.size, // เก็บขนาดไฟล์ไว้ด้วย
        };
        setFormData((prev) => ({
          ...prev,
          paymentEvidence: [...prev.paymentEvidence, fileData],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle file view
  const handleViewFile = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  // Print receipt function
  const printReceipt = () => {
    const getPickupPointName = (pickupPointId) => {
      const point = pickupPoints.find((p) => p.WarehouseID === pickupPointId);
      return point ? point.WarehouseName : "-";
    };

    const totalAmount = formData.VaccineDetails.reduce(
      (sum, detail) => sum + detail.TotalPrice,
      0
    );
    const thaiAmountText = numberToThaiText(totalAmount);

    // ตรวจสอบ formData.RequestDate ก่อนส่งไป convertToThDate
    const requestDate = formData.RequestDate instanceof Date && isValid(formData.RequestDate)
      ? formData.RequestDate
      : null;

    const printContent = `
      <html>
        <head>
          <title>Receipt - ${formData.TransactionID}</title>
          <style>
            body { font-family: Sarabun, Arial, sans-serif; margin: 40px; }
            h1, h2 { text-align: center; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
            .grid-item { margin-bottom: 8px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 32px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .total { text-align: right; font-weight: bold; margin-bottom: 16px; }
            .thai-text { text-align: right; margin-bottom: 32px; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h1>ใบเสร็จรับเงิน</h1>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${formData.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่จำหน่าย:</span> ${requestDate ? convertToThDate(requestDate) : "-"}
            </div>
            <div class="grid-item">
              <span>จุดจำหน่าย:</span> ${getPickupPointName(formData.PickupPoint) || "-"}
            </div>
            <div class="grid-item">
              <span>เลขประจำตัวประชาชน:</span> ${formData.FarmerID || "-"}
            </div>
            <div class="grid-item">
              <span>ชื่อเกษตรกร:</span> ${formData.FarmerName || "-"}
            </div>
            <div class="grid-item">
              <span>เบอร์โทรติดต่อ:</span> ${formData.FarmerTel || "-"}
            </div>
            <div class="grid-item">
              <span>E-mail Address:</span> ${formData.FarmerEmail || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>วัตถุประสงค์การใช้งาน:</span> ${formData.UsageObjective || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่จำหน่าย</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th>จำนวน (ขวด)</th>
                <th>จำนวนโดส</th>
                <th>ราคาต่อหน่วย</th>
                <th>มูลค่ารวม</th>
              </tr>
            </thead>
            <tbody>
              ${
                formData.VaccineDetails.length > 0
                  ? formData.VaccineDetails.map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td>${detail.RequestQuantity.toLocaleString()}</td>
                          <td>${detail.DoseCount.toLocaleString()}</td>
                          <td>${detail.UnitPrice.toLocaleString()}</td>
                          <td>${detail.TotalPrice.toLocaleString()}</td>
                        </tr>
                      `
                    ).join("")
                  : `
                        <tr>
                          <td colspan="7" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="total">
            รวมทั้งสิ้น: ${totalAmount.toLocaleString()} บาท
          </div>
          <div class="thai-text">
            (${thaiAmountText})
          </div>
          <div class="signature">
            ___________________________<br>
            ผู้รับเงิน
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    if (!newWindow) {
      alert("ไม่สามารถเปิดหน้าต่างพิมพ์ได้ กรุณาปิดการบล็อกป๊อปอัป");
      return;
    }
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchSale = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrdersByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const sale = response.VaccineSaleOrder;
        const details = response.VaccineDetails || [];
        
        // Debug: Log the entire response
        console.log("API Response:", response);
        console.log("Sale object:", sale);
        console.log("PaymentEvidence from API:", sale.paymentEvidence);
        // Debug: Log payment evidence data
        console.log("Loaded payment evidence:", sale.paymentEvidence);
        console.log("Payment evidence length:", sale.paymentEvidence?.length || 0);
        console.log("Payment evidence type:", typeof sale.paymentEvidence);
        console.log("Payment evidence structure:", JSON.stringify(sale.paymentEvidence, null, 2));
        
        // Validate and clean payment evidence data
        let validatedPaymentEvidence = [];
        if (sale.paymentEvidence && Array.isArray(sale.paymentEvidence)) {
          // Handle nested JSON strings and filter out any invalid entries
          const processedItems = [];
          
          sale.paymentEvidence.forEach(item => {
            if (typeof item === 'string') {
              // Try to parse JSON string
              try {
                const parsed = JSON.parse(item);
                if (Array.isArray(parsed)) {
                  // If parsed item is an array, add all items
                  processedItems.push(...parsed);
                } else if (parsed && typeof parsed === 'object') {
                  // If parsed item is an object, add it
                  processedItems.push(parsed);
                }
              } catch (parseError) {
                console.warn("Failed to parse PaymentEvidence item in frontend:", item, parseError);
              }
            } else if (item && typeof item === 'object') {
              // Direct object
              processedItems.push(item);
            }
          });
          
          // Filter out any invalid entries
          const validItems = processedItems.filter(item => 
            item && 
            typeof item === 'object' && 
            item.name && 
            item.type && 
            item.data &&
            typeof item.name === 'string' &&
            typeof item.type === 'string' &&
            typeof item.data === 'string'
          );
          
          // Remove duplicates based on file name and data
          const uniqueItems = [];
          const seenItems = new Set();
          
          validItems.forEach(item => {
            const key = `${item.name}_${item.data.substring(0, 100)}`; // Use first 100 chars of data as key
            if (!seenItems.has(key)) {
              seenItems.add(key);
              uniqueItems.push(item);
            } else {
              console.log("Removing duplicate item in frontend:", item.name);
            }
          });
          
          validatedPaymentEvidence = uniqueItems;
        }
        console.log("Validated payment evidence:", validatedPaymentEvidence);
        console.log("Validated payment evidence length:", validatedPaymentEvidence.length);
        console.log("Validated payment evidence structure:", JSON.stringify(validatedPaymentEvidence, null, 2));
        console.log("Validated payment evidence is array:", Array.isArray(validatedPaymentEvidence));
        console.log("Details array:", details);
        console.log("Details length:", details.length);

        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find(
            (v) => v.VaccineID === detail.VaccineID
          );
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            UnitPrice: detail.UnitPrice,
            TotalPrice: detail.TotalPrice,
            DoseCount: doseCount,
          };
        });
        
        const newFormData = {
          TransactionID: sale.TransactionID,
          FarmerID: sale.FarmerID || "",
          FarmerName: sale.FarmerName || "",
          FarmerTel: sale.FarmerTel || "",
          FarmerEmail: sale.FarmerEmail || "",
          UsageObjective: sale.UsageObjective || "",
          PickupPoint: sale.PickupPoint || "",
          ReservedBy: sale.ReservedBy || "",
          RequestDate: sale.RequestDate ? new Date(sale.RequestDate) : new Date(),
          VaccineDetails: updatedDetails,
          paymentEvidence: validatedPaymentEvidence, // Load existing files (validated)
        };
        
        console.log("Setting formData:", newFormData);
        console.log("FormData VaccineDetails length:", newFormData.VaccineDetails.length);
        console.log("FormData paymentEvidence length:", newFormData.paymentEvidence.length);
        console.log("FormData paymentEvidence is array:", Array.isArray(newFormData.paymentEvidence));
        
        setFormData(newFormData);
        
        // Debug: Check if formData was set correctly
        setTimeout(() => {
          console.log("FormData after setting:", formData);
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching sale:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Form input change:", { name, value });
    console.log("Current formData:", formData);
    if (name === "PickupPoint" && formData.VaccineDetails.length > 0) {
      alert("ไม่สามารถแก้ไขจุดจำหน่ายได้ เนื่องจากมีรายการวัคซีนแล้ว");
      return;
    }
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "PickupPoint") {
        // Reset vaccine selection when PickupPoint changes
        setNewVaccine({ id: "", lotId: "", unitreq: "" });
        //setInventory([]);
      }
      return updated;
    });
    validateField(name, value);
  };

  const handleVaccineInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Vaccine input change:", { name, value });
    if (name === "id" && !formData.PickupPoint) {
      alert("กรุณาเลือกจุดจำหน่ายก่อนเลือกวัคซีน");
      return;
    }
    setNewVaccine((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "id") {
        // Reset lotId and unitreq when vaccine changes
        return { ...updated, lotId: "", unitreq: "" };
      }
      return updated;
    });
  };

  const handleQuantityChange = async (vaccineId, lotId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    console.log("handleQuantityChange:", { vaccineId, lotId, newQuantity });
    console.log("Current inventory:", inventory);

    // Find the Lot in inventory
    let selectedLot = inventory.find(
      (item) => item.VaccineID === vaccineId && item.LotID === lotId
    );

    // If Lot not found, try fetching it
    if (!selectedLot && formData.PickupPoint && sessionId) {
      console.log(
        `Lot ${lotId} not found, fetching inventory for VaccineID ${vaccineId}`
      );
      await fetchLotInventory(vaccineId, formData.PickupPoint);
      selectedLot = inventory.find(
        (item) => item.VaccineID === vaccineId && item.LotID === lotId
      );
    }
    if (!selectedLot) {
      console.warn(
        `Lot ${lotId} not found for VaccineID ${vaccineId} in inventory`
      );
      alert(`ไม่พบ Lot ${lotId} สำหรับวัคซีน ${vaccineId} ในคลัง`);
      return;
    }

    // Validate quantity against TotalQuantity
    if (newQuantity > selectedLot.TotalQuantity) {
      alert(
        `จำนวนที่ขอจำหน่าย (${newQuantity.toLocaleString()}) เกินจำนวนคงคลังของ Lot ${lotId} (${selectedLot.TotalQuantity.toLocaleString()})`
      );
      return;
    }

    // Update VaccineDetails if quantity is valid
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize ||
          1;
        const newTotalPrice = newQuantity * detail.UnitPrice;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return {
          ...detail,
          RequestQuantity: newQuantity,
          TotalPrice: newTotalPrice,
          DoseCount: newDoseCount,
        };
      }
      return detail;
    });
    console.log("Updating VaccineDetails:", updatedDetails);
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "PickupPoint":
      case "FarmerName":
      case "FarmerTel":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.PickupPoint &&
      formData.FarmerName &&
      formData.FarmerTel &&
      Object.keys(errors).length === 0 &&
      formData.VaccineDetails.length > 0 // เพิ่มเงื่อนไขว่าต้องมีรายการวัคซีนอย่างน้อยหนึ่งรายการ
    );
  };

  const handleAddVaccine = () => {
    console.log("Attempting to add vaccine:", newVaccine);
    if (!newVaccine.id || !newVaccine.lotId) {
      alert("กรุณาเลือกวัคซีนและ Lot ที่ต้องการจำหน่าย");
      return;
    }
    if (!newVaccine.unitreq || newVaccine.unitreq <= 0) {
      alert("กรุณาระบุจำนวนที่มากกว่า 0");
      return;
    }

    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newVaccine.id
    );
    const selectedLot = inventory.find(
      (item) => item.LotID === newVaccine.lotId
    );
    if (!selectedVaccine || !selectedLot) {
      alert("วัคซีนหรือ Lot ที่เลือกไม่พบ");
      return;
    }

    const requestQuantity = parseInt(newVaccine.unitreq);
    if (requestQuantity > selectedLot.TotalQuantity) {
      alert(
        `จำนวนที่ขอจำหน่าย (${requestQuantity.toLocaleString()}) เกินจำนวนคงคลังของ Lot ${
          selectedLot.LotID
        } (${selectedLot.TotalQuantity.toLocaleString()})`
      );
      return;
    }

    const packageSize = selectedVaccine.PackageSize || 1;
    const doseCount = Math.ceil(requestQuantity * packageSize);
    const unitPrice = selectedVaccine.PriceDistribution || 350;
    const totalPrice = requestQuantity * unitPrice;

    const vaccineData = {
      VaccineID: newVaccine.id,
      name: selectedVaccine.VaccineNameTH,
      LotID: newVaccine.lotId,
      RequestQuantity: requestQuantity,
      UnitPrice: unitPrice,
      TotalPrice: totalPrice,
      DoseCount: doseCount,
    };

    console.log("Adding vaccine to VaccineDetails:", vaccineData);
    setFormData({
      ...formData,
      VaccineDetails: [...formData.VaccineDetails, vaccineData],
    });
    setNewVaccine({ id: "", lotId: "", unitreq: "" });
  };

  const handleRemoveVaccine = async (row) => {
    setFormData({
        ...formData,
        VaccineDetails: formData.VaccineDetails.filter(
          (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
        ),
      });
      // Refresh inventory for the removed vaccine
      if (formData.PickupPoint && sessionId) {
        await fetchLotInventory(row.VaccineID, formData.PickupPoint);
      }
  };

const handleSave = async () => {
  if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนและเพิ่มรายการวัคซีนที่จำหน่ายอย่างน้อยหนึ่งรายการ");
    return;
  }

  setIsLoading(true);
  try {
        // Submit form data with paymentEvidence
        const submitData = { ...formData };
        
        // Validate and clean payment evidence before submission
        let validatedPaymentEvidence = [];
        if (submitData.paymentEvidence && Array.isArray(submitData.paymentEvidence)) {
          // Handle nested JSON strings and filter out any invalid entries
          const processedItems = [];
          
          submitData.paymentEvidence.forEach(item => {
            if (typeof item === 'string') {
              // Try to parse JSON string
              try {
                const parsed = JSON.parse(item);
                if (Array.isArray(parsed)) {
                  // If parsed item is an array, add all items
                  processedItems.push(...parsed);
                } else if (parsed && typeof parsed === 'object') {
                  // If parsed item is an object, add it
                  processedItems.push(parsed);
                }
              } catch (parseError) {
                console.warn("Failed to parse PaymentEvidence item before submission:", item, parseError);
              }
            } else if (item && typeof item === 'object') {
              // Direct object
              processedItems.push(item);
            }
          });
          
          // Filter out any invalid entries
          const validItems = processedItems.filter(item => 
            item && 
            typeof item === 'object' && 
            item.name && 
            item.type && 
            item.data &&
            typeof item.name === 'string' &&
            typeof item.type === 'string' &&
            typeof item.data === 'string'
          );
          
          // Remove duplicates based on file name and data
          const uniqueItems = [];
          const seenItems = new Set();
          
          validItems.forEach(item => {
            const key = `${item.name}_${item.data.substring(0, 100)}`; // Use first 100 chars of data as key
            if (!seenItems.has(key)) {
              seenItems.add(key);
              uniqueItems.push(item);
            } else {
              console.log("Removing duplicate item before submission:", item.name);
            }
          });
          
          validatedPaymentEvidence = uniqueItems;
        }
        submitData.paymentEvidence = validatedPaymentEvidence;
        
        console.log("Submitting data with paymentEvidence:", submitData.paymentEvidence);
        console.log("Submitting paymentEvidence length:", submitData.paymentEvidence?.length);
        console.log("Submitting paymentEvidence structure:", JSON.stringify(submitData.paymentEvidence, null, 2));
        console.log("Submitting paymentEvidence is array:", Array.isArray(submitData.paymentEvidence));
    const response = await apiService({
      method: "POST",
      url: "/createVaccineSaleOrders",
      data: submitData,
      sessionId: sessionId || "",
    });

    if (response.result === "success") {
      const transactionId = response.TransactionID;
      setFormData({ ...formData, TransactionID: transactionId });
      setIsExisting(true);

      // Files are already included in the main request

      alert("บันทึกใบจำหน่ายสำเร็จ! เลขที่เอกสาร: " + transactionId);
    }
  } catch (error) {
    console.error("API Error:", error.message);
    alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
  } finally {
    setIsLoading(false);
      setIsConfirmModalOpen(false); // ปิด Modal หลังบันทึก
  }
};

  // ฟังก์ชันสำหรับเปิด Modal ยืนยัน
  const handleOpenConfirmModal = () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนและเพิ่มรายการวัคซีนที่จำหน่ายอย่างน้อยหนึ่งรายการ");
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faListOl} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faVial} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) => (
        <Box>
          {isExisting ? (
            row.RequestQuantity.toLocaleString()
          ) : (
            <Input
              type="number"
              value={row.RequestQuantity}
              onChange={(e) =>
                handleQuantityChange(row.VaccineID, row.LotID, e.target.value)
              }
              min={0}
              size="sm"
              width="100px"
            />
          )}
        </Box>
      ),
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวนโดส</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      cell: (row) => <Box>{row.DoseCount.toLocaleString()}</Box>,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faDollarSign} />
          <span>ราคาต่อหน่วย</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice,
      sortable: true,
      cell: (row) => <Box>{row.UnitPrice.toLocaleString()}</Box>,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>มูลค่ารวม</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice,
      sortable: true,
      cell: (row) => <Box>{row.TotalPrice.toLocaleString()}</Box>,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>การดำเนินการ</span>
        </HStack>
      ),
      cell: (row) =>
        !isExisting && (
          <IconButton.Delete
            icon={faTrash}
            onClick={() => handleRemoveVaccine(row)}
            label="ลบรายการ"
            width="100px"
          />
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  // Filter and sort Lots (FIFO by ProductionDate)
  const availableLots = inventory
    .filter(
      (item) => item.VaccineID === newVaccine.id && item.TotalQuantity > 0
    )
    .sort((a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate));

  // Set default LotID when availableLots changes
  useEffect(() => {
    console.log("Available Lots updated:", availableLots);
    if (availableLots.length > 0 && !newVaccine.lotId && !isInventoryLoading) {
      console.log("Setting default LotID:", availableLots[0].LotID);
      setNewVaccine((prev) => ({ ...prev, lotId: availableLots[0].LotID }));
    }
  }, [availableLots, newVaccine.lotId, isInventoryLoading]);

  // Debug: Track formData changes
  useEffect(() => {
    console.log("FormData changed:", formData);
    console.log("FormData paymentEvidence:", formData.paymentEvidence);
    console.log("FormData paymentEvidence length:", formData.paymentEvidence?.length);
    console.log("FormData paymentEvidence is array:", Array.isArray(formData.paymentEvidence));
  }, [formData]);

  if (!sessionId) {
    return null;
  }

  if (isLoading && isExisting) {
    return (
      <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
        <StandardPageTitle title="รายการจำหน่ายวัคซีน" />
        <Box textAlign="center" py={20}>
          <Spinner size="xl" color="blue.500" />
          <Text mt={4} fontSize="lg">กำลังโหลดข้อมูล...</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายการจำหน่ายวัคซีน" />
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ข้อมูลเอกสารการจำหน่ายวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem>
            <FormControl isInvalid={errors.TransactionID}>
              <FormLabel>เลขที่เอกสาร</FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID || ""}
                isReadOnly
                colorScheme="iBlue"
              />
              {/* Debug: Show current formData */}
              {console.log("Rendering TransactionID:", formData.TransactionID)}
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>วันที่จำหน่าย</FormLabel>
              <CustomDatePicker
                  name="RequestDate"
                  value={formData.RequestDate}
                  onChange={handleInputChange}
                  minDate={
                    new Date(new Date().setDate(new Date().getDate() - 7))
                  } // 7 วันย้อนหลังจากวันนี้
                  maxDate={new Date()} // วันนี้
                  colorScheme="iBlue"
                  isReadOnly={isExisting}
                />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired isInvalid={errors.PickupPoint}>
              <FormLabel>จุดจำหน่าย</FormLabel>
              {isExisting ? (
                <Input
                  value={
                    pickupPoints.find(
                      (point) => point.WarehouseID === formData.PickupPoint
                    )?.WarehouseName ||
                    formData.PickupPoint ||
                    "-"
                  }
                  isReadOnly
                  colorScheme="iBlue"
                />
              ) : (
                <>
                <Select
                  name="PickupPoint"
                  value={formData.PickupPoint}
                  onChange={handleInputChange}
                  placeholder="เลือกจุดจำหน่าย"
                  isDisabled={formData.VaccineDetails.length > 0}
                >
                  {pickupPoints.map((point) => (
                    <option key={point.WarehouseID} value={point.WarehouseID}>
                      {point.WarehouseName}
                    </option>
                  ))}
                </Select>
                {formData.VaccineDetails.length > 0 && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    ไม่สามารถแก้ไขจุดจำหน่ายได้ เนื่องจากมีรายการวัคซีนแล้ว
                  </Text>
                  )}
                </>                
              )}
              <FormErrorMessage>{errors.PickupPoint}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel>เลขประจำตัวประชาชน</FormLabel>
              <Input
                name="FarmerID"
                value={formData.FarmerID}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={2}>
            <FormControl isRequired isInvalid={!!errors.FarmerName}>
              <FormLabel>ชื่อเกษตรกร</FormLabel>
              <Input
                name="FarmerName"
                value={formData.FarmerName}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
              <FormErrorMessage>{errors.FarmerName}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.FarmerTel}>
              <FormLabel>เบอร์โทรติดต่อ</FormLabel>
              <Input
                name="FarmerTel"
                value={formData.FarmerTel}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
              <FormErrorMessage>{errors.FarmerTel}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel>E-mail Address</FormLabel>
              <Input
                name="FarmerEmail"
                value={formData.FarmerEmail}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl mb={4}>
              <FormLabel>วัตถุประสงค์การใช้งาน / รายละเอียดเพิ่มเติม</FormLabel>
              <Textarea
                name="UsageObjective"
                value={formData.UsageObjective}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl mb={4}>
              <FormLabel>หลักฐานการชำระเงิน</FormLabel>
              {!isExisting ? (
                <Dropzone
                  onDrop={handleFileDrop}
                  accept={{
                    "image/jpeg": [".jpg", ".jpeg"],
                    "image/png": [".png"],
                    "application/pdf": [".pdf"],
                  }}
                  multiple
                >
                  {({ getRootProps, getInputProps }) => (
                    <Box
                      {...getRootProps()}
                      p={4}
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      textAlign="center"
                      cursor="pointer"
                    >
                      <input {...getInputProps()} />
                      <Text>
                        ลากและวางไฟล์ที่นี่ หรือคลิกเพื่อเลือกไฟล์ (JPEG, PNG, PDF)
                      </Text>
               <Text fontSize="xs" color="orange.500" mt={2}>
                 ⚠️ ขนาดไฟล์ไม่ควรเกิน 700KB เพื่อป้องกันปัญหาในการบันทึกข้อมูล
               </Text>
                    </Box>
                  )}
                </Dropzone>
              ) : null}
                {/* Debug: Show paymentEvidence */}
                {console.log("Rendering paymentEvidence:", formData.paymentEvidence)}
                {console.log("Rendering paymentEvidence length:", formData.paymentEvidence?.length)}
                {console.log("Rendering paymentEvidence structure:", JSON.stringify(formData.paymentEvidence, null, 2))}
                {console.log("Rendering paymentEvidence is array:", Array.isArray(formData.paymentEvidence))}
                {(formData.paymentEvidence && formData.paymentEvidence.length > 0) && (
                  <Box mt={4}>
                    <Text fontWeight="bold" mb={3}>ไฟล์หลักฐานการชำระเงิน:</Text>
                    <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
                      {formData.paymentEvidence.map((file, index) => (
                        <Box
                          key={index}
                          borderWidth={1}
                          borderRadius="lg"
                          p={3}
                          bg="gray.50"
                          _hover={{ bg: "gray.100" }}
                        >
                          {file.type.startsWith("image/") ? (
                            <VStack spacing={2}>
                              <Image
                                src={file.data}
                                alt={file.name}
                                maxW="100%"
                                maxH="120px"
                                objectFit="cover"
                                borderRadius="md"
                                cursor="pointer"
                                onClick={() => handleViewFile(file)}
                                _hover={{ opacity: 0.8 }}
                              />
                              <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                                {file.name}
                              </Text>
                              {file.size && (
                                <Text fontSize="xs" color="gray.500" textAlign="center">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              )}
                              {!isExisting && (
                                <IconButton.Delete
                                  icon={faTrash}
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      paymentEvidence: prev.paymentEvidence.filter(
                                        (_, i) => i !== index
                                      ),
                                    }))
                                  }
                                  label="ลบไฟล์"
                                  size="sm"
                                />
                              )}
                            </VStack>
                          ) : file.type === "application/pdf" ? (
                            <VStack spacing={2}>
                       <Box
                         bg="red.100"
                         p={4}
                         borderRadius="md"
                         textAlign="center"
                         cursor="pointer"
                         onClick={() => {
                           // Create a blob from base64 data and open it directly
                           try {
                             const byteCharacters = atob(file.data.split(',')[1]);
                             const byteNumbers = new Array(byteCharacters.length);
                             for (let i = 0; i < byteCharacters.length; i++) {
                               byteNumbers[i] = byteCharacters.charCodeAt(i);
                             }
                             const byteArray = new Uint8Array(byteNumbers);
                             const blob = new Blob([byteArray], { type: 'application/pdf' });
                             const url = URL.createObjectURL(blob);
                             window.open(url, '_blank');
                           } catch (error) {
                             console.error('Error opening PDF:', error);
                             handleViewFile(file); // Fallback to modal view
                           }
                         }}
                         _hover={{ bg: "red.200" }}
                       >
                         <FontAwesomeIcon icon={faFileLines} size="2x" color="#e53e3e" />
                         <Text fontSize="xs" mt={1} color="red.600">
                           PDF
                         </Text>
                       </Box>
                              <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                                {file.name}
                              </Text>
                              {file.size && (
                                <Text fontSize="xs" color="gray.500" textAlign="center">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              )}
                              {!isExisting && (
                                <IconButton.Delete
                                  icon={faTrash}
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      paymentEvidence: prev.paymentEvidence.filter(
                                        (_, i) => i !== index
                                      ),
                                    }))
                                  }
                                  label="ลบไฟล์"
                                  size="sm"
                                />
                              )}
                            </VStack>
                          ) : (
                            <VStack spacing={2}>
                              <Box
                                bg="gray.100"
                                p={4}
                                borderRadius="md"
                                textAlign="center"
                                cursor="pointer"
                                onClick={() => handleViewFile(file)}
                                _hover={{ bg: "gray.200" }}
                              >
                                <FontAwesomeIcon icon={faFileLines} size="2x" color="#718096" />
                                <Text fontSize="xs" mt={1} color="gray.600">
                                  ไฟล์
                                </Text>
                              </Box>
                              <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                                {file.name}
                              </Text>
                              {file.size && (
                                <Text fontSize="xs" color="gray.500" textAlign="center">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              )}
                              {!isExisting && (
                                <IconButton.Delete
                                  icon={faTrash}
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      paymentEvidence: prev.paymentEvidence.filter(
                                        (_, i) => i !== index
                                      ),
                                    }))
                                  }
                                  label="ลบไฟล์"
                                  size="sm"
                                />
                              )}
                            </VStack>
                          )}
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                )}
                
         {/* แสดงข้อความเมื่อไม่มีไฟล์แนบ */}
         {isExisting && (!formData.paymentEvidence || formData.paymentEvidence.length === 0) && (
           <Box mt={4} p={4} bg="gray.50" borderRadius="md" textAlign="center">
             <Text color="gray.500" fontSize="sm">
               ยังไม่มีไฟล์หลักฐานการชำระเงิน
             </Text>
             <Text color="gray.400" fontSize="xs" mt={2}>
               หมายเหตุ: ไฟล์ขนาดใหญ่กว่า 2MB อาจไม่แสดงผลได้
             </Text>
           </Box>
         )}
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      {!isExisting && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="เลือกวัคซีนที่ต้องการจำหน่าย"
          bgColor="iPurple.500"
          icon={faSyringe}
        />
          <Grid templateColumns="repeat(3, 1fr)"  gap={6} p={5}>
            <GridItem>
              <FormControl>
                <FormLabel>เลือกวัคซีน</FormLabel>
                <Select
                  name="id"
                  value={newVaccine.id}
                  onChange={handleVaccineInputChange}
                  placeholder="เลือกวัคซีน"
                  isDisabled={!formData.PickupPoint}
                >
                  {vaccineOptions.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
                {!formData.PickupPoint && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    กรุณาเลือกจุดจำหน่ายก่อนเลือกวัคซีน
                  </Text>
                )}
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Lot No.</FormLabel>
                {isInventoryLoading ? (
                  <HStack>
                    <Spinner size="sm" />
                    <Text>กำลังโหลด Lot...</Text>
                  </HStack>
                ) : (
                  <>
                    <Select
                      name="lotId"
                      value={newVaccine.lotId}
                      onChange={handleVaccineInputChange}
                      placeholder={
                        availableLots.length > 0
                          ? "เลือก Lot"
                          : "ไม่มี Lot คงเหลือ"
                      }
                      isDisabled={
                        !newVaccine.id || !formData.PickupPoint || isInventoryLoading
                      }
                    >
                      {availableLots.map((lot) => (
                        <option 
                          key={`${lot.VaccineID}-${lot.LotID}`}
                          value={lot.LotID}
                        >
                          {`${lot.LotID} (จำนวนคงเหลือ: ${lot.TotalQuantity.toLocaleString()} ขวด, Exp.${new Date(
                            lot.ExpiryDate
                          ).toLocaleDateString("th-TH", {
                            day: "numeric",
                            month: "numeric",
                            year: "numeric",
                          })})`}
                        </option>
                      ))}
                    </Select>
                    {newVaccine.id &&
                      !isInventoryLoading &&
                      availableLots.length === 0 && (
                        <Text color="red.500" fontSize="sm" mt={2}>
                          ไม่มี Lot คงเหลือสำหรับวัคซีนนี้
                        </Text>
                      )}
                  </>
                )}
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>จำนวน</FormLabel>
                <Input
                  name="unitreq"
                  type="number"
                  value={newVaccine.unitreq}
                  onChange={handleVaccineInputChange}
                  placeholder="จำนวน (ขวด)"
                  isDisabled={
                    !newVaccine.id || !newVaccine.lotId || isInventoryLoading
                  }
                />
              </FormControl>
            </GridItem>
          </Grid>
          <HStack mt={4} justify="flex-end">
            <Button
              leftIcon={<FontAwesomeIcon icon={faSyringe} />}
              colorScheme="green"
              onClick={handleAddVaccine}
              isDisabled={
                !newVaccine.id ||
                !newVaccine.lotId ||
                !newVaccine.unitreq ||
                newVaccine.unitreq <= 0 ||
                isInventoryLoading
              }
            >
              เพิ่มรายการ
            </Button>
          </HStack>
        </Box>
      )}

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
      <StandardHeader
          title="รายการวัคซีนที่จำหน่าย"
          bgColor="iOrange.500"
          icon={faTable}
        />
        <Box p={{ base: 0, md: 5 }} overflowX="auto">
          {/* Debug: Show VaccineDetails */}
          {console.log("Rendering VaccineDetails:", formData.VaccineDetails)}
          <DataTable
            columns={columns}
            data={formData.VaccineDetails || []}
            customStyles={tableCustomStyles}
            noDataComponent="ไม่มีรายการวัคซีน"
            subHeader
            subHeaderComponent={
              <Box width="100%" textAlign="right" p={2}>
                <strong>รวมจำนวน (ขวด): </strong>
                {formData.VaccineDetails.reduce(
                  (sum, detail) => sum + (detail.RequestQuantity || 0),
                  0
                ).toLocaleString()}{" "}
                | <strong> รวมมูลค่ารวม (บาท): </strong>
                {formData.VaccineDetails.reduce(
                  (sum, detail) => sum + (detail.TotalPrice || 0),
                  0
                ).toLocaleString()}
              </Box>
            }
          />
        </Box>
        
        {/* สรุปข้อมูลการจำหน่าย */}
        {formData.VaccineDetails && formData.VaccineDetails.length > 0 && (
          <Box
            mt={5}
            p={5}
            bg="blue.50"
            borderRadius="lg"
            borderWidth={1}
            borderColor="blue.200"
          >
            <Heading size="md" mb={4} color="blue.700">
              สรุปข้อมูลการจำหน่าย
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem>
                <Box
                  p={4}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    จำนวนชนิดวัคซีน
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {new Set(formData.VaccineDetails.map((d) => d.VaccineID)).size}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    ชนิด
                  </Text>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p={4}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    จำนวนขวด
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {formData.VaccineDetails.reduce(
                      (sum, detail) => sum + (detail.RequestQuantity || 0),
                      0
                    ).toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    ขวด
                  </Text>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p={4}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    จำนวนโดส
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                    {formData.VaccineDetails.reduce(
                      (sum, detail) => sum + (detail.DoseCount || 0),
                      0
                    ).toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    โดส
                  </Text>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p={4}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    ราคารวม
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                    {formData.VaccineDetails.reduce(
                      (sum, detail) => sum + (detail.TotalPrice || 0),
                      0
                    ).toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    บาท
                  </Text>
                </Box>
              </GridItem>
            </Grid>
          </Box>
        )}
      </Box>

      <Box p={5} textAlign="right">
        <HStack spacing={5} mb={5}>
          <IconButton.Back
            onClick={() => navigate("/vaccinesalelist")}
            label="ย้อนกลับ"
          />
          {isExisting && (
            <Button
              leftIcon={<FontAwesomeIcon icon={faPrint} />}
              colorScheme="blue"
              onClick={printReceipt}
            >
              พิมพ์ใบเสร็จรับเงิน
            </Button>
          )}
          {!isExisting && (
            <IconButton.Save
              onClick={handleOpenConfirmModal} // เปลี่ยนให้เปิด Modal แทนการบันทึกทันที
              isLoading={isLoading}
              isDisabled={!isFormValid()}
            />
          )}
        </HStack>
      </Box>

        {/* Modal for viewing files */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="6xl">
          <ModalOverlay />
          <ModalContent maxW="90vw" maxH="90vh">
            <ModalHeader>
              <HStack spacing={3}>
                <FontAwesomeIcon icon={faFileLines} />
                <Text>ดูไฟล์: {selectedFile?.name}</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedFile?.type.startsWith("image/") ? (
                <Box textAlign="center">
                  <Image 
                    src={selectedFile.data} 
                    alt={selectedFile.name} 
                    maxW="100%" 
                    maxH="70vh"
                    objectFit="contain"
                    borderRadius="md"
                    boxShadow="lg"
                  />
                  <Text mt={3} fontSize="sm" color="gray.600">
                    คลิกที่ภาพเพื่อดูขนาดเต็ม
                  </Text>
                </Box>
              ) : selectedFile?.type === "application/pdf" ? (
                <VStack spacing={4}>
                  <Box
                    bg="red.50"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    borderWidth={1}
                    borderColor="red.200"
                  >
                    <FontAwesomeIcon icon={faFileLines} size="3x" color="#e53e3e" />
                    <Text fontSize="lg" fontWeight="bold" mt={3} color="red.700">
                      ไฟล์ PDF
                    </Text>
                    <Text fontSize="sm" color="red.600" mt={1}>
                      {selectedFile.name}
                    </Text>
                  </Box>
                   <Button
                     onClick={() => {
                       // Create a blob from base64 data and open it
                       const byteCharacters = atob(selectedFile.data.split(',')[1]);
                       const byteNumbers = new Array(byteCharacters.length);
                       for (let i = 0; i < byteCharacters.length; i++) {
                         byteNumbers[i] = byteCharacters.charCodeAt(i);
                       }
                       const byteArray = new Uint8Array(byteNumbers);
                       const blob = new Blob([byteArray], { type: 'application/pdf' });
                       const url = URL.createObjectURL(blob);
                       window.open(url, '_blank');
                     }}
                     colorScheme="red"
                     leftIcon={<FontAwesomeIcon icon={faFileLines} />}
                     size="lg"
                     width="100%"
                   >
                     เปิดไฟล์ PDF
                   </Button>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    คลิกเพื่อเปิดไฟล์ PDF ในเบราว์เซอร์
                  </Text>
                </VStack>
              ) : (
                <Box textAlign="center" py={8}>
                  <FontAwesomeIcon icon={faFileLines} size="3x" color="#718096" />
                  <Text fontSize="lg" fontWeight="bold" mt={3} color="gray.600">
                    ไม่สามารถแสดงไฟล์นี้ได้
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    {selectedFile?.name}
                  </Text>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

      {/* Modal for confirming save */}
      <Modal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ยืนยันการบันทึกใบจำหน่าย</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="lg" mb={4}>
              กรุณาตรวจสอบข้อมูลก่อนบันทึก หลังจากบันทึกแล้วจะไม่สามารถแก้ไขรายการได้
            </Text>
            <Box>
              <Text fontWeight="bold">ชื่อเกษตรกร:</Text>
              <Text>{formData.FarmerName || "-"}</Text>
            </Box>
            <Box mt={4}>
              <Text fontWeight="bold">รายการวัคซีนที่จำหน่าย:</Text>
              {formData.VaccineDetails.length > 0 ? (
                <VStack align="start" mt={2}>
                  {formData.VaccineDetails.map((detail, index) => (
                    <Text key={index}>
                      {`${detail.name} (Lot: ${detail.LotID}, จำนวน: ${detail.RequestQuantity.toLocaleString()} ขวด)`}
                    </Text>
                  ))}
                </VStack>
              ) : (
                <Text>ไม่มีรายการวัคซีน</Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="backScheme"
              mr={3}
              onClick={() => setIsConfirmModalOpen(false)}
            >
              ยกเลิก
            </Button>
            <Button
              colorScheme="saveScheme"
              onClick={handleSave}
              isLoading={isLoading}
            >
              ยืนยัน
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VaccineSaleForm;
